// client/src/components/pages/LexiStar/Reports/TripReportDocument.tsx
//
// The trip report (ST-00188 M4-R2; FR-WB-010; LS-09 §5.5). One component for
// the screen and the paper — the renderer prints this same tree — laid out as
// the LS-09 wireframe: header; MAP | WEIGHTS; DISTANCE & TIME | FUEL;
// EXCEPTIONS | ESTIMATED COST TO TRANSPORTER; TIMELINE; then (page 2 on paper)
// stops and idle, dwell per site, the plan(s) — both plans on a re-planned
// trip — the message log summary and the weighbridge tickets. AMENDED trips
// carry the watermark on every page. Every figure carries its provenance mark
// or a "how was this calculated?" workings block, printed open on paper.

import { useMemo, type ComponentType } from 'react';
import CorridorMapJs from '../Approvals/CorridorMap';
import { STAGE_LABELS } from '../StatusBoard/statusLanguage';
import {
  fmtDateTime,
  fmtDuration,
  fmtKg,
  fmtKm,
  fmtL,
  fmtNum,
  fmtRand,
  fmtSignedL,
  fmtTime,
} from '../StatusBoard/format';
import { weighVariance, approvedExtraKm, spanSeconds, VARIANCE_TOL_KG, VARIANCE_TOL_PCT } from '../TripDetail/tripMath';
import {
  channelLabel,
  costByKind,
  dwellBySite,
  kindLabel,
  messageSummary,
  stageDurations,
  stopsIdleSummary,
  tripExceptions,
  tripPlans,
  numOrNull,
} from './reportLogic';
import { How, Prov, ProvenanceKey, ReportDoc, Row, Section, type Fact } from './reportAtoms';
import TrackSketch, { type SketchLine } from './TrackSketch';
import type { Availability, TripReportData } from './tripReportData';
import type { Loose, TripCost } from '../../../../types/lexistar-reports';

/** Approvals/CorridorMap.jsx, typed for the props this page passes. */
const CorridorMap = CorridorMapJs as unknown as ComponentType<{
  idPrefix?: string;
  routes?: Array<{ key: string; feature: unknown }>;
  track?: unknown;
  deviations?: Array<{ geometry: unknown; approved: boolean }>;
  stops?: Array<{ lat: number; lon: number }>;
  className?: string;
}>;

const rand2 = (v: number | null) =>
  v === null ? '—' : `${v < 0 ? '−' : ''}R${Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const str = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));
const obj = (v: unknown): Loose => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Loose) : {});

const UNAVAILABLE: Record<Availability, string> = {
  ok: '',
  forbidden: 'Not shown — your role cannot see this part of the trip.',
  unavailable: 'Not available from the server yet.',
  skipped: 'Not shown — your role cannot see this part of the trip.',
};

const RECORD_WORDS: Record<string, string> = {
  OPEN: 'Open record',
  SIGNED_OFF: 'Signed off',
  AMENDED: 'Amended',
};

const PRESENCE: Record<string, { glyph: string; text: string; attention: boolean }> = {
  present: { glyph: '✔', text: 'presence verified', attention: false },
  absent: { glyph: '✖', text: 'no presence at the weighbridge', attention: true },
  unknown: { glyph: '?', text: 'presence unknown', attention: false },
};

function Presence({ presence, confidence }: { presence: unknown; confidence: unknown }) {
  const meta = PRESENCE[String(presence)] || PRESENCE.unknown!;
  return (
    <span
      className={meta.attention ? 'lxr-alert' : 'lxr-muted'}
      title={confidence !== null && confidence !== undefined ? `${meta.text} (${String(confidence)}% confidence)` : meta.text}
    >
      <span aria-hidden="true">{meta.glyph}</span> {meta.text}
    </span>
  );
}

function Missing({ state }: { state: Availability }) {
  return <p className="lxr-empty">{UNAVAILABLE[state]}</p>;
}

// ── Sections ────────────────────────────────────────────────────────────────

function Weights({ trip, matches }: { trip: Loose; matches: Loose[] }) {
  const byType = (t: string) => matches.find((m) => m.ticket_type === t && m.state !== 'rejected');
  const dispatch = byType('dispatch');
  const receipt = byType('receipt');
  const v = weighVariance(trip) as { kg: number; pct: number | null; tolerance: number; breach: boolean } | null;
  const line = (label: string, m: Loose | undefined, net: unknown) => (
    <Row label={label}>
      {net !== null && net !== undefined ? (
        <>
          <span className="lxr-strong">{fmtKg(net)}</span>{' '}
          {(obj(m?.ticket).weighed_at || m?.weighed_at) ? (
            <span className="lxr-muted">{fmtTime(obj(m?.ticket).weighed_at || m?.weighed_at)} </span>
          ) : null}
          {m ? <Presence presence={m.presence} confidence={m.presence_confidence} /> : null}
        </>
      ) : (
        <span className="lxr-muted">no ticket matched yet</span>
      )}
    </Row>
  );
  const sign = (n: number) => (n > 0 ? '−' : n < 0 ? '+' : '');
  return (
    <Section title="Weights" aside={<span>● weighbridge</span>}>
      <dl className="lxr-dl">
        {line('Dispatch net', dispatch, trip.dispatch_net_kg ?? obj(dispatch?.ticket).net_kg ?? dispatch?.net_kg)}
        {line('Receipt net', receipt, trip.receipt_net_kg ?? obj(receipt?.ticket).net_kg ?? receipt?.net_kg)}
        <Row label="Variance">
          {v ? (
            <>
              <span className={v.breach ? 'lxr-alert' : 'lxr-strong'}>
                {sign(v.kg)}
                {fmtKg(Math.abs(v.kg))} ({v.pct !== null ? `${sign(v.kg)}${Math.abs(v.pct * 100).toFixed(2)}%` : '—'})
              </span>{' '}
              <span className={`lxr-tag ${v.breach ? 'lxr-tag--attention' : ''}`}>
                {v.breach ? 'outside tolerance' : 'within tolerance'}
              </span>
            </>
          ) : (
            '—'
          )}
        </Row>
      </dl>
      {v && (
        <How>
          <div>
            variance = dispatch net − receipt net = {fmtNum(trip.dispatch_net_kg)} − {fmtNum(trip.receipt_net_kg)} ={' '}
            {fmtNum(v.kg)} kg
          </div>
          <div>
            tolerance = max({(VARIANCE_TOL_PCT * 100).toFixed(1)}% × {fmtNum(trip.dispatch_net_kg)}, {VARIANCE_TOL_KG} kg) ={' '}
            {fmtNum(v.tolerance)} kg
          </div>
          <div>
            breach = |{fmtNum(v.kg)}| &gt; {fmtNum(v.tolerance)} → {v.breach ? 'yes' : 'no'}
          </div>
        </How>
      )}
    </Section>
  );
}

function DistanceTime({ data, cost }: { data: TripReportData; cost: TripCost | null }) {
  const { trip, legs, replay, timeline } = data;
  const current = legs.filter((l) => l.state !== 'replaced');
  const extra = approvedExtraKm(trip) as number | null;
  const adjusted = numOrNull(trip.adjusted_km ?? trip.planned_km);
  const actual = numOrNull(trip.actual_km);
  const variance = actual !== null && adjusted !== null ? actual - adjusted : null;
  const plannedLoaded = current
    .filter((l) => l.load_state === 'loaded')
    .reduce((a, l) => a + (numOrNull(l.planned_km) ?? 0), 0);
  const plannedEmpty = current
    .filter((l) => l.load_state !== 'loaded')
    .reduce((a, l) => a + (numOrNull(l.planned_km) ?? 0), 0);
  // Loaded / empty actuals only when the server gives them (cost model inputs or the trip row).
  const kmLoaded = numOrNull(trip.km_loaded ?? trip.actual_loaded_km ?? cost?.how?.distance.km_loaded);
  const kmLoadedBasis = trip.km_loaded != null || trip.actual_loaded_km != null ? 'measured' : cost?.how?.distance.km_loaded_basis ?? null;
  const kmEmpty = kmLoaded !== null && actual !== null ? Math.max(0, actual - kmLoaded) : null;
  const stages = stageDurations(replay?.stage_bands ?? [], timeline);
  const idle = stopsIdleSummary(replay?.stops ?? [], replay?.idles ?? []);
  const duration = spanSeconds(trip.dispatched_at, trip.completed_at) as number | null;

  return (
    <Section title="Distance & time">
      <dl className="lxr-dl">
        <Row label="Planned">{fmtKm(trip.planned_km)}</Row>
        <Row label="Adjusted">
          {fmtKm(adjusted)}
          {extra !== null && extra > 0 && <span className="lxr-muted"> (+{fmtNum(extra)} approved)</span>}
        </Row>
        <Row label="Actual">
          <span className="lxr-strong">{fmtKm(actual)}</span>
          <Prov provenance={trip.distance_provenance || 'derived'} />
        </Row>
        <Row label="Loaded · empty">
          {kmLoaded !== null ? (
            <>
              {fmtKm(kmLoaded)} · {fmtKm(kmEmpty)}
              <span className="lxr-muted"> ({kmLoadedBasis === 'apportioned' ? 'apportioned' : kmLoadedBasis || 'actual'})</span>
            </>
          ) : (
            <>
              {fmtKm(plannedLoaded)} · {fmtKm(plannedEmpty)} <span className="lxr-muted">planned</span>
            </>
          )}
        </Row>
        <Row label="Variance vs adjusted">
          <span className={variance !== null && variance > 0 ? 'lxr-strong' : ''}>
            {variance === null ? '—' : `${variance > 0 ? '+' : variance < 0 ? '−' : ''}${fmtKm(Math.abs(variance))}`}
          </span>
        </Row>
        <Row label="Route average">
          {trip.route_avg_km != null ? fmtKm(trip.route_avg_km) : <span className="lxr-muted">— (fewer than five trips)</span>}
        </Row>
        <Row label="Duration">
          <span className="lxr-strong">{fmtDuration(duration)}</span>
        </Row>
        {stages.length > 0 && (
          <Row label="By stage">
            <span className="lxr-stages">
              {stages.map((s) => (
                <span key={s.stage}>
                  <span className="lxr-muted">{s.label}</span> {fmtDuration(s.seconds)}
                </span>
              ))}
            </span>
          </Row>
        )}
        <Row label="Idle">
          {data.replayState === 'ok' ? (
            <>
              {fmtDuration(idle.idleSeconds)}
              <span className="lxr-muted"> ({fmtDuration(idle.idleOutsideSeconds)} outside known zones)</span>
            </>
          ) : (
            <span className="lxr-muted">—</span>
          )}
        </Row>
      </dl>
      <How>
        <div>
          planned = Σ leg planned km = {current.map((l) => fmtNum(l.planned_km)).join(' + ') || '—'} = {fmtNum(trip.planned_km)} km
        </div>
        <div>
          adjusted = planned + approved extra km = {fmtNum(trip.planned_km)} + {fmtNum(extra ?? 0)} = {fmtNum(adjusted)} km
        </div>
        <div>actual = distance from dispatch to completion (odometer when measured, else GPS)</div>
        <div>
          variance = actual − adjusted = {fmtNum(actual)} − {fmtNum(adjusted)} = {variance === null ? '—' : fmtNum(variance)} km
        </div>
        {kmLoaded !== null && (
          <div>
            loaded km {kmLoadedBasis === 'apportioned' ? 'apportioned from the actual total by planned leg km' : 'as recorded'}; empty = actual − loaded
          </div>
        )}
        <div>duration = completed − dispatched; stage times from the engine’s stage spans</div>
      </How>
    </Section>
  );
}

function Fuel({ data, showRand }: { data: TripReportData; showRand: boolean }) {
  const { trip, legs, fuel } = data;
  const f = fuel || {};
  const current = legs.filter((l) => l.state !== 'replaced');
  const original = numOrNull(f.budget_original_l ?? trip.budget_original_l);
  const adjusted = numOrNull(f.budget_adjusted_l ?? trip.budget_adjusted_l) ?? original;
  const used = numOrNull(f.consumed_l ?? trip.consumed_l);
  const provenance = f.provenance ?? f.consumed_provenance ?? trip.consumed_provenance;
  const variance = numOrNull(f.variance_l) ?? (used !== null && adjusted !== null ? adjusted - used : null);
  const pinned = obj(f.pinned ?? trip.pinned);
  const price = numOrNull(pinned.fuel_price_per_l);
  const varianceR = numOrNull(f.variance_r) ?? (variance !== null && price !== null ? variance * price : null);
  const observed = obj(f.observed);
  const norm = (state: string) =>
    numOrNull(obj(obj(pinned.norms)[state]).l_per_100km) ??
    numOrNull(obj(f.norms)[state === 'loaded' ? 'loaded_l_per_100km' : 'empty_l_per_100km']);
  return (
    <Section title="Fuel">
      <dl className="lxr-dl">
        <Row label="Allowance">
          {adjusted !== null && adjusted !== original ? (
            <>
              {fmtL(original)} → <span className="lxr-strong">{fmtL(adjusted)}</span> adjusted
            </>
          ) : (
            fmtL(original)
          )}
        </Row>
        <Row label="Used">
          <span className="lxr-strong">{fmtL(used)}</span>
          <Prov provenance={provenance} />
          {provenance ? <span className="lxr-muted"> {String(provenance).toLowerCase()}</span> : null}
        </Row>
        <Row label="L/100 km loaded · empty">
          {observed.loaded_l_per_100km != null || observed.empty_l_per_100km != null ? (
            <>
              {fmtNum(observed.loaded_l_per_100km, 1)} · {fmtNum(observed.empty_l_per_100km, 1)}
            </>
          ) : (
            <span className="lxr-muted">—</span>
          )}
        </Row>
        <Row label="Variance vs adjusted">
          <span className={variance !== null && variance < 0 ? 'lxr-alert' : 'lxr-strong'}>{fmtSignedL(variance)}</span>
          {showRand && varianceR !== null && <span className="lxr-muted"> ({fmtRand(varianceR)})</span>}
        </Row>
        {current.map((l) => (
          <Row key={`fuel-${String(l.id ?? l.seq)}`} label={<span className="lxr-muted">&nbsp;&nbsp;{l.load_state === 'loaded' ? 'Loaded' : 'Empty'} leg</span>}>
            {fmtL(l.consumed_l)}
            <Prov provenance={l.consumed_provenance} /> <span className="lxr-muted">/ {fmtL(l.budget_l)}</span>
            {l.over_consumption && (
              <>
                {' '}
                <span className="lxr-tag lxr-tag--attention">+{fmtNum(l.over_consumption.excess_l, 1)} L {l.over_consumption.certainty}</span>
              </>
            )}
          </Row>
        ))}
      </dl>
      <How>
        <div>allowance = Σ leg km × norm ÷ 100 (+ idle allowance, contingency) — pinned at dispatch</div>
        {current.map((l) => (
          <div key={`calc-${String(l.id ?? l.seq)}`}>
            {l.load_state} leg: {fmtNum(l.planned_km)} km → {fmtL(l.budget_l)}
            {norm(String(l.load_state)) !== null && ` (norm ${fmtNum(norm(String(l.load_state)), 1)} L/100 km)`}
          </div>
        ))}
        <div>
          adjusted = original + approved extra km × norm ÷ 100 = {fmtL(original)} + {fmtNum((adjusted ?? 0) - (original ?? 0))} L ={' '}
          {fmtL(adjusted)}
        </div>
        <div>
          variance = adjusted − used = {fmtNum(adjusted)} − {fmtNum(used)} = {fmtSignedL(variance)} (negative = over)
        </div>
        {showRand && price !== null && <div>rand = litres × pinned price R{price.toFixed(2)}/L</div>}
        <div>over-consumption: used − allowance &gt; max(3%, 2 L); confirmed only when measured. It informs — it never blocks.</div>
      </How>
    </Section>
  );
}

function Exceptions({ data }: { data: TripReportData }) {
  const items = tripExceptions({
    trip: data.trip,
    legs: data.legs,
    deviations: data.deviations,
    matches: data.matches,
    events: (data.replay?.events ?? []) as Loose[],
    timeLabel: (iso) => fmtTime(iso),
  });
  return (
    <Section title="Exceptions" aside={items.length ? `${items.length}` : undefined}>
      {items.length === 0 ? (
        <p className="lxr-empty">No exceptions on this trip.</p>
      ) : (
        <ul className="lxr-dl" style={{ listStyle: 'none', padding: 0 }}>
          {items.map((i) => (
            <li key={i.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '3px 0', borderBottom: '1px solid var(--lxr-rule)' }}>
              <span>
                {i.attention ? <span className="lxr-alert" aria-hidden="true">! </span> : <span aria-hidden="true">• </span>}
                {i.text}
              </span>
              <span className={`lxr-tag ${i.attention ? 'lxr-tag--attention' : ''}`}>{i.state}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Cost({ cost, state }: { cost: TripCost | null; state: Availability }) {
  if (state !== 'ok') {
    return (
      <Section title="Estimated cost to transporter">
        <Missing state={state} />
      </Section>
    );
  }
  if (!cost) {
    return (
      <Section title="Estimated cost to transporter">
        <p className="lxr-empty">No cost yet — it is estimated when the trip completes.</p>
      </Section>
    );
  }
  const kinds = costByKind(cost.lines);
  const h = cost.how;
  return (
    <Section title="Estimated cost to transporter" aside={<span>○ estimated{cost.state === 'final' ? ' · final' : ''}</span>}>
      <dl className="lxr-dl">
        {kinds.map((k) => (
          <Row key={k.kind} label={k.label}>
            {k.amount === null ? '—' : fmtRand(k.amount)}
            {k.lines.some((l) => l.source === 'manual') && <span className="lxr-muted"> incl. manual</span>}
          </Row>
        ))}
        <Row label={<span className="lxr-strong">Total</span>}>
          <span className="lxr-strong">{fmtRand(cost.total_zar)}</span>
        </Row>
        <Row label="Per tonne · per t·km">
          {cost.cost_per_t !== null ? `${fmtRand(cost.cost_per_t)}/t` : '—'} ·{' '}
          {cost.cost_per_tkm !== null ? `${rand2(cost.cost_per_tkm)}/t·km` : '—'}
        </Row>
      </dl>
      {!cost.stored && <p className="lxr-empty">Preview — the cost is stored when the trip completes.</p>}
      <How>
        {h?.formula.map((f, i) => (
          <div key={`f${i}`}>{f}</div>
        ))}
        {h && (
          <>
            <div>
              fuel = {fmtNum(h.fuel.litres)} L ({h.fuel.basis || '—'}
              {h.fuel.provenance ? `, ${h.fuel.provenance}` : ''}) × {h.fuel.price_per_l !== null ? `R${h.fuel.price_per_l.toFixed(2)}` : '—'}/L
              {h.fuel_price_pinned ? ' (price pinned at dispatch)' : ''}
            </div>
            <div>
              running = {fmtNum(h.distance.km_total)} km ({h.distance.km_total_basis || '—'}) ×{' '}
              {h.distance.rate_per_km !== null ? rand2(h.distance.rate_per_km) : '—'}/km
              {h.rate_card_pinned ? ' (rate card pinned)' : ''}
            </div>
            <div>
              tolls = Σ plaza fees for class {h.toll_class || cost.toll_class || '—'}
              {h.tolls.length ? `: ${h.tolls.map((t) => `${t.name} ${t.fee !== null ? rand2(t.fee) : '—'}`).join(' + ')}` : ' (none on the route)'}
            </div>
            <div>
              driver time = {fmtNum(h.time.hours, 1)} h{h.time.basis ? ` (${h.time.basis.replace(/_/g, ' ')})` : ''} ×{' '}
              {h.time.driver_rate_per_h !== null ? rand2(h.time.driver_rate_per_h) : '—'}/h
            </div>
            <div>
              per tonne = total ÷ {fmtNum(cost.net_receipt_t ?? h.tonnes.value, 2)} t{h.tonnes.basis ? ` (${h.tonnes.basis})` : ''}; per t·km = total ÷ (t × {fmtNum(cost.km_loaded ?? h.distance.km_loaded)} loaded km)
            </div>
            {h.missing.length > 0 && <div>missing inputs: {h.missing.join(', ')}</div>}
            {h.stale && h.current_total_zar !== null && (
              <div>inputs have moved on since: a recompute would give {fmtRand(h.current_total_zar)}</div>
            )}
          </>
        )}
        {!h && <div>fuel = litres × price; running = km × rate; tolls = Σ plaza fees; driver = hours × rate; other = manual lines</div>}
        {cost.lines
          .filter((l) => l.source === 'manual' || l.kind === 'other')
          .map((l, i) => (
            <div key={`m${i}`}>
              {l.kind}: {l.label} {fmtRand(l.amount_zar)}
              {l.note ? ` — ${l.note}` : ''}
            </div>
          ))}
      </How>
    </Section>
  );
}

function Timeline({ timeline }: { timeline: Loose[] }) {
  const rows = [...timeline].sort((a, b) => Date.parse(String(a.at)) - Date.parse(String(b.at)));
  return (
    <Section title="Timeline" wide>
      {rows.length === 0 ? (
        <p className="lxr-empty">Nothing has happened on this trip yet.</p>
      ) : (
        <ol className="lxr-timeline" aria-label="Trip timeline">
          {rows.map((t, i) => (
            <li key={String(t.id ?? i)} title={t.actor ? `by ${String(t.actor)}` : undefined}>
              <time>{fmtTime(t.at)}</time>
              <span>{kindLabel(t.kind)}</span>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

function StopsIdle({ data }: { data: TripReportData }) {
  if (data.replayState !== 'ok' || !data.replay) {
    return (
      <Section title="Stops and idle">
        <Missing state={data.replayState} />
      </Section>
    );
  }
  const s = stopsIdleSummary(data.replay.stops as Loose[], data.replay.idles as Loose[]);
  return (
    <Section title="Stops and idle">
      <dl className="lxr-dl">
        <Row label="Stops">
          {fmtNum(s.stops)} · {fmtDuration(s.stoppedSeconds)}
        </Row>
        <Row label="Idle periods">
          {fmtNum(s.idles)} · {fmtDuration(s.idleSeconds)}
        </Row>
        <Row label="Idle outside known zones">{fmtDuration(s.idleOutsideSeconds)}</Row>
      </dl>
      {s.longest.length > 0 && (
        <>
          <h3>Longest stops</h3>
          <table className="lxr-table">
            <thead>
              <tr>
                <th>From</th>
                <th>Place</th>
                <th className="lxr-num">Duration</th>
              </tr>
            </thead>
            <tbody>
              {s.longest.map((r, i) => (
                <tr key={`st${i}`}>
                  <td>{fmtTime(new Date(r.at).toISOString())}</td>
                  <td>
                    {r.place}
                    {!r.inZone && <small>outside known zones</small>}
                  </td>
                  <td className="lxr-num">{fmtDuration(r.seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Section>
  );
}

const PURPOSE_WORDS: Record<string, string> = {
  source: 'Source',
  destination: 'Destination',
  weighbridge: 'Weighbridge',
  tipping: 'Tipping zone',
  delay: 'Delay zone',
  border: 'Border post',
  depot: 'Depot',
  fuel: 'Fuel station',
};

function Dwell({ data }: { data: TripReportData }) {
  if (data.replayState !== 'ok' || !data.replay) {
    return (
      <Section title="Dwell per site">
        <Missing state={data.replayState} />
      </Section>
    );
  }
  const rows = dwellBySite(data.replay.visits as Loose[]);
  return (
    <Section title="Dwell per site" aside="● geofence visits">
      {rows.length === 0 ? (
        <p className="lxr-empty">No site visits recorded on this trip.</p>
      ) : (
        <table className="lxr-table">
          <thead>
            <tr>
              <th>Site</th>
              <th>Role</th>
              <th className="lxr-num">Visits</th>
              <th className="lxr-num">Dwell</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.site}>
                <td>{r.site}</td>
                <td>{r.purpose ? PURPOSE_WORDS[r.purpose] || kindLabel(r.purpose) : '—'}</td>
                <td className="lxr-num">{fmtNum(r.visits)}</td>
                <td className="lxr-num">{fmtDuration(r.seconds)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function Plans({ data }: { data: TripReportData }) {
  const plans = tripPlans(data.legs, { planVersion: data.trip.plan_version, timeline: data.timeline });
  const replanned = plans.length > 1;
  return (
    <Section
      title={replanned ? 'Plans — this trip was re-planned' : 'Plan'}
      aside={replanned ? `${plans.length} plans` : undefined}
      wide
    >
      {plans.map((p) => (
        <div key={p.version} style={{ marginTop: replanned ? 6 : 0 }}>
          {replanned && (
            <h3>
              Plan {p.version} {p.current ? '(current)' : `(replaced${p.replacedAt ? ` ${fmtDateTime(p.replacedAt)}` : ''})`}
              {p.reason && <span className="lxr-muted"> — {p.reason}</span>}
            </h3>
          )}
          <table className="lxr-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Leg</th>
                <th>From → to</th>
                <th>Route</th>
                <th className="lxr-num">Planned</th>
                <th className="lxr-num">Allowance</th>
              </tr>
            </thead>
            <tbody>
              {p.legs.map((l, i) => (
                <tr key={`${p.version}-${String(l.seq ?? i)}`}>
                  <td>{l.seq ?? i + 1}</td>
                  <td>
                    {kindLabel(l.kind)} <span className="lxr-muted">({l.loadState})</span>
                    {l.state === 'replaced' && <small>replaced</small>}
                  </td>
                  <td>
                    {l.from} → {l.to}
                  </td>
                  <td>{l.route || <span className="lxr-muted">proposed</span>}</td>
                  <td className="lxr-num">{fmtKm(l.plannedKm)}</td>
                  <td className="lxr-num">{fmtL(l.budgetL)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>Plan {p.version} total</td>
                <td className="lxr-num">{fmtKm(p.plannedKm)}</td>
                <td className="lxr-num">{fmtL(p.budgetL)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ))}
    </Section>
  );
}

function Messages({ data }: { data: TripReportData }) {
  if (data.messagesState !== 'ok') {
    return (
      <Section title="Message log">
        <Missing state={data.messagesState} />
      </Section>
    );
  }
  const m = messageSummary(data.messages);
  if (m.total === 0) {
    return (
      <Section title="Message log">
        <p className="lxr-empty">No messages about this trip.</p>
      </Section>
    );
  }
  return (
    <Section title="Message log" aside={m.costZar !== null ? `${fmtRand(m.costZar)} messaging cost` : undefined}>
      <dl className="lxr-dl">
        <Row label="Messages">
          {fmtNum(m.total)} · {fmtNum(m.outbound)} sent · {fmtNum(m.inbound)} received
          {m.failed > 0 && <span className="lxr-alert"> · {fmtNum(m.failed)} failed</span>}
        </Row>
        <Row label="By channel">
          {m.byChannel.map((c) => `${channelLabel(c.channel)} ${c.count}`).join(' · ')}
        </Row>
      </dl>
      <h3>Latest</h3>
      <table className="lxr-table">
        <tbody>
          {m.recent.map((r) => (
            <tr key={r.id}>
              <td style={{ width: '1%', whiteSpace: 'nowrap' }}>{fmtTime(r.at)}</td>
              <td style={{ width: '1%', whiteSpace: 'nowrap' }}>
                {r.direction === 'in' ? '← ' : '→ '}
                {channelLabel(r.channel)}
              </td>
              <td>{r.what}</td>
              <td className="lxr-num lxr-muted">{r.state}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

function Tickets({ matches }: { matches: Loose[] }) {
  const tickets = matches.filter((m) => m.state !== 'rejected');
  return (
    <Section title="Weighbridge tickets">
      {tickets.length === 0 ? (
        <p className="lxr-empty">No tickets matched to this trip.</p>
      ) : (
        <div className="lxr-tickets">
          {tickets.map((t) => (
            <figure key={String(t.id)} style={{ margin: 0 }}>
              <div className="lxr-ticket-frame" aria-label="Ticket image not available">
                Ticket image
                <br />
                not captured yet
              </div>
              <figcaption style={{ fontSize: 11, marginTop: 4 }}>
                <span className="lxr-strong">{kindLabel(t.ticket_type)}</span> #{str(t.ticket_number) || str(t.source_key) || '—'}
                <br />
                <span className="lxr-muted">
                  {fmtTime(t.weighed_at || obj(t.ticket).weighed_at)} · {fmtKg(t.net_kg ?? obj(t.ticket).net_kg)} net
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </Section>
  );
}

// ── The document ────────────────────────────────────────────────────────────

export default function TripReportDocument({
  data,
  print,
  showRand,
  generatedAt,
}: {
  data: TripReportData;
  print: boolean;
  showRand: boolean;
  generatedAt?: string | null;
}) {
  const { trip, legs } = data;
  const current = legs.filter((l) => l.state !== 'replaced');
  const loaded = current.find((l) => l.kind === 'loaded') || legs.find((l) => l.kind === 'loaded');
  const combination = obj(trip.combination);
  const trailers = Array.isArray(combination.trailers) ? combination.trailers.map(String) : [];
  const vehicle =
    [
      str(combination.horse) || str(trip.registration) || String(trip.asset_key || '').replace(/^(reg|asset):/, ''),
      trailers.join('/'),
    ]
      .filter(Boolean)
      .join(' + ') || '—';
  const load = obj(trip.load);
  const recordState = String(trip.record_state || 'OPEN');
  const amended = recordState === 'AMENDED';
  const plans = tripPlans(legs, { planVersion: trip.plan_version, timeline: data.timeline });
  const route = loaded
    ? `${str(loaded.route_code) || str(loaded.route_name) || (loaded.route_id ? 'Route' : 'Proposed route')}${loaded.route_version != null ? ` v${String(loaded.route_version)}` : ''}`
    : '—';

  const facts: Fact[] = [
    { label: 'Truck', value: vehicle, wide: true },
    { label: 'Driver', value: str(trip.driver_name) || str(trip.driver_tag) || 'No driver identified' },
    { label: 'Route', value: route },
    { label: 'Consignor', value: str(load.consignor) || '—' },
    { label: 'Consignee', value: str(load.consignee) || '—' },
    { label: 'Material', value: str(load.material) || '—' },
    { label: 'Order ref', value: str(load.order_ref) || '—' },
    { label: 'Planned tonnes', value: load.planned_tonnes != null ? `${fmtNum(load.planned_tonnes, 1)} t` : '—' },
    { label: 'Dispatched', value: fmtDateTime(trip.dispatched_at) },
    { label: 'Completed', value: fmtDateTime(trip.completed_at) },
  ];

  const sketchPlanned: SketchLine[] = useMemo(() => {
    const fromReplay = (data.replay?.planned_legs ?? []) as Loose[];
    const withGeom = fromReplay.filter((l) => l.geometry);
    if (withGeom.length) return withGeom.map((l) => ({ geometry: l.geometry, replaced: !!l.replaced }));
    return data.routes.filter((r) => r.feature).map((r) => ({ geometry: r.feature, replaced: r.replaced }));
  }, [data.replay, data.routes]);

  const deviationLines = useMemo(() => {
    const own = data.deviations
      .filter((d) => d.track)
      .map((d) => ({ geometry: d.track, approved: String(d.state) === 'APPROVED' }));
    if (own.length) return own;
    return ((data.replay?.deviations ?? []) as Loose[])
      .filter((d) => d.geometry || d.track)
      .map((d) => ({ geometry: d.geometry ?? d.track, approved: d.approved === true }));
  }, [data.deviations, data.replay]);

  const track = data.track ?? (data.replay?.track?.length ? data.replay.track : null);
  const stops = ((data.replay?.stops ?? []) as Loose[])
    .filter((s) => s.lat != null && s.lon != null)
    .map((s) => ({ lat: Number(s.lat), lon: Number(s.lon) }));

  const sketch = (
    <TrackSketch planned={sketchPlanned} track={track} deviations={deviationLines} stops={stops} />
  );

  const amendedBanner = amended ? (
    <div className="lxr-banner lxr-banner--alert" role="note" aria-label="Amended watermark">
      <strong>{str(trip.watermark) || `Amended ${fmtDateTime(trip.amended_at)}`}</strong>
      <span>This trip was changed after it was signed off. Every report, statement and export that includes it carries this watermark.</span>
    </div>
  ) : recordState === 'SIGNED_OFF' ? (
    <div className="lxr-banner" role="note">
      <strong>Signed off</strong>
      <span>
        {trip.signed_off_by ? `by ${String(trip.signed_off_by)} ` : ''}
        {trip.signed_off_at ? `on ${fmtDateTime(trip.signed_off_at)}` : ''} — the record is frozen; changes go through an amendment.
      </span>
    </div>
  ) : null;

  return (
    <ReportDoc
      print={print}
      label={`Trip report ${String(trip.reference || '')}`}
      reportName="Trip report"
      title={`Trip ${String(trip.reference || '')}`}
      subtitle={
        <>
          {loaded ? `${String(loaded.from_name || '—')} → ${String(loaded.to_name || '—')}` : '—'}
          {load.material ? ` · ${String(load.material)}` : ''}
          {load.order_ref ? ` · ${String(load.order_ref)}` : ''}
        </>
      }
      chips={
        <>
          <span className="lxr-chip lxr-chip--ink">{STAGE_LABELS[String(trip.stage) as keyof typeof STAGE_LABELS] || String(trip.stage || '—')}</span>
          <span className={`lxr-chip ${amended ? 'lxr-chip--alert' : ''}`} data-record-state={recordState}>
            {RECORD_WORDS[recordState] || kindLabel(recordState)}
          </span>
          {plans.length > 1 && <span className="lxr-chip lxr-chip--dashed">Re-planned · plan {plans[plans.length - 1]!.version}</span>}
        </>
      }
      facts={facts}
      watermark={amended ? 'AMENDED' : null}
      banner={amendedBanner}
      generatedAt={generatedAt}
      footer={
        <>
          <span>
            Trip {String(trip.reference || '')} · <ProvenanceKey /> · cost is an estimate (rate card and fuel price pinned to the trip)
          </span>
          <span>{amended ? str(trip.watermark) || 'AMENDED' : RECORD_WORDS[recordState] || ''}</span>
        </>
      }
    >
      <div className="lxr-grid">
        <Section title="Map" aside="planned vs actual">
          {print ? (
            sketch
          ) : (
            <>
              <div className="lxr-screen-only">
                <CorridorMap
                  idPrefix="report"
                  routes={data.routes
                    .filter((r) => r.feature && !r.replaced)
                    .map((r) => ({ key: r.key, feature: r.feature }))}
                  track={data.track ?? null}
                  deviations={deviationLines}
                  stops={stops}
                  className="relative z-0 h-[340px] overflow-hidden border border-slate-200"
                />
              </div>
              <div className="lxr-print-only">{sketch}</div>
            </>
          )}
        </Section>
        <Weights trip={trip} matches={data.matches} />
        <DistanceTime data={data} cost={data.cost} />
        <Fuel data={data} showRand={showRand} />
        <Exceptions data={data} />
        <Cost cost={data.cost} state={data.costState} />
        <Timeline timeline={data.timeline} />
      </div>
      <div className="lxr-grid" data-testid="report-detail">
        <StopsIdle data={data} />
        <Dwell data={data} />
        <Plans data={data} />
        <Messages data={data} />
        <Tickets matches={data.matches} />
      </div>
    </ReportDoc>
  );
}
